import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, LogOut, Globe, Plus, LayoutDashboard, Menu, X, ShoppingCart, Mail, Home, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import NotificationBell from './NotificationBell';

export default function AppHeader() {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const isApproved = user?.company?.status === 'approved';

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <header className="bg-white border-b border-border sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <Link to={user?.role === 'admin' ? '/admin' : '/dashboard'} className="flex items-center gap-2">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-accent to-accent/70 rounded-lg flex items-center justify-center">
              <Building2 size={20} className="text-white sm:hidden" />
              <Building2 size={24} className="text-white hidden sm:block" />
            </div>
            <span className="text-xl sm:text-2xl font-heading font-black tracking-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {t('appName')}
            </span>
          </Link>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-3">
            {user?.role !== 'admin' && (
              <>
                <Link to="/dashboard" data-testid="dashboard-link">
                  <button className="px-4 py-2 text-sm font-medium text-foreground hover:text-accent transition-colors">
                    <span className="flex items-center gap-1.5">
                      <LayoutDashboard size={18} />
                      {t('dashboard')}
                    </span>
                  </button>
                </Link>
                <Link to="/marketplace" data-testid="marketplace-link">
                  <button className="px-4 py-2 text-sm font-medium text-foreground hover:text-accent transition-colors">
                    {t('marketplace')}
                  </button>
                </Link>
                <Link to="/messages" data-testid="messages-link">
                  <button className="px-4 py-2 text-sm font-medium text-foreground hover:text-accent transition-colors">
                    {t('messages')}
                  </button>
                </Link>
              </>
            )}
            <Link to="/profile" data-testid="profile-link">
              <button className="px-4 py-2 text-sm font-medium text-foreground hover:text-accent transition-colors flex items-center gap-1.5">
                <User size={16} />
                {language === 'ar' ? 'البروفايل' : 'Profile'}
              </button>
            </Link>
            <NotificationBell />
            <button onClick={toggleLanguage} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="language-toggle">
              <Globe size={18} />
              {language === 'ar' ? 'English' : 'عربي'}
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground hover:text-destructive transition-colors" data-testid="logout-btn">
              <LogOut size={20} />
              {t('logout')}
            </button>
          </div>

          {/* Mobile: Language + Hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <NotificationBell />
            <button onClick={toggleLanguage} className="p-2 text-muted-foreground hover:text-primary" data-testid="language-toggle-mobile">
              <Globe size={20} />
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-foreground" data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-3 pb-3 border-t border-border pt-3 space-y-1" data-testid="mobile-menu">
            {user?.role !== 'admin' && (
              <>
                <Link to="/dashboard" onClick={closeMenu} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 text-foreground font-medium">
                  <LayoutDashboard size={20} className="text-accent" /> {t('dashboard')}
                </Link>
                <Link to="/marketplace" onClick={closeMenu} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 text-foreground font-medium">
                  <ShoppingCart size={20} className="text-muted-foreground" /> {t('marketplace')}
                </Link>
                <Link to="/messages" onClick={closeMenu} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 text-foreground font-medium">
                  <Mail size={20} className="text-muted-foreground" /> {t('messages')}
                </Link>
                <Link to="/profile" onClick={closeMenu} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 text-foreground font-medium" data-testid="mobile-profile-link">
                  <User size={20} className="text-muted-foreground" /> {language === 'ar' ? 'البروفايل' : 'Profile'}
                </Link>
              </>
            )}
            <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-red-50 text-destructive font-medium w-full">
              <LogOut size={20} /> {t('logout')}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
